import { Flex, useAccordionItemContext } from '@chakra-ui/react';
import {
  AccountTypeEnum,
  BillGroupEntityEnum,
  ConditionOperatorEnum,
  ConditionOperatorTypeEnum,
  CustomerPortalEntityEnum,
  EntityGroupEnum,
  ExpressionOperatorEnum,
  FieldTypeEnum,
  ICondition,
  InvoiceEntityEnum,
  IRule,
  IRuleCondition,
  IRuleFormResponse,
  MCustomSelectProps,
  QuoteEntityEnum,
  RecordAutomationActionFields,
  RecordAutomationUiFieldTypeEnum,
  ReferenceEntityEnum,
  RuleConditionEntityEnum,
  RuleEntityEnumDisplay,
  RuleFormField,
  RuleSchema,
  RuleStatusEnum,
  RuleStatusEnumDisplay,
  RuleTypeEnum,
  TypeOperator,
  TypeOperatorByFieldType,
  TypeOperatorEnum,
  UserTypeEnum,
  ValidateRuleResponse,
} from '@monetize/types/app';
import { GetListApiConfig, Maybe } from '@monetize/types/common';
import { FC, ReactNode } from 'react';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { composeGetQuery } from '../../../api/utils';
import {
  MAccordionCustomButton,
  MFlex,
  MIcon,
  MText,
} from '../../../components/Monetize';
import { Highlighter } from '../../../components/Monetize/MPageSearchInput/Highlighter';
import { DISCOUNT_STATUS_ENUM_DISPLAY } from '../../../constants/discounts';
import {
  OFFERING_STATUS_DISPLAY,
  OFFERING_TYPES_ENUM_DISPLAY,
  RATE_BILLING_FREQUENCY_DISPLAY,
  SUBSCRIPTION_TIMING_OPTIONS,
} from '../../../constants/offerings';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../constants/products';
import { QuoteStatusEnumDisplay } from '../../../constants/quotes';
import { TEAM_TYPE_ENUM_DISPLAY } from '../../../constants/teams';
import { USER_TYPE_DISPLAY } from '../../../constants/users';
import { nullifyEmptyStrings } from '../../../utils/misc';

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`. Shared by
// RuleFormTestBillGroupRule.v3.tsx and RuleFormTestQuoteRule.v3.tsx, whose
// only use of `isExpanded` is feeding it straight into this button - the
// (large, otherwise-unaffected) rest of each accordion body is passed
// through untouched as `children`.
export const TestRuleAccordionButton = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton isExpanded={isExpanded} label={label} />
      {children}
    </>
  );
};

export type ReferenceOptions = Pick<
  MCustomSelectProps,
  | 'additionalSearchParams'
  | 'itemSearch'
  | 'loadAll'
  | 'getByIdEndpointFn'
  | 'itemTitle'
  | 'omitContainsItemSearch'
  | 'itemValue'
  | 'endpoint'
  | 'renderItemContent'
  | 'popOverContentProps'
  | 'returnItem'
>;

// If the operator changes value, some types require clearing out the value
export const compatibleOperatorTypes = [
  new Set([
    ConditionOperatorEnum.EQUAL,
    ConditionOperatorEnum.NOT_EQUAL,
    ConditionOperatorEnum.GREATER_THAN,
    ConditionOperatorEnum.GREATER_THAN_INCLUSIVE,
    ConditionOperatorEnum.LESS_THAN,
    ConditionOperatorEnum.LESS_THAN_INCLUSIVE,
    ConditionOperatorEnum.STARTS_WITH,
    ConditionOperatorEnum.ENDS_WITH,
    ConditionOperatorEnum.CONTAINS,
    ConditionOperatorEnum.NOT_CONTAINS,
  ]),
  new Set([ConditionOperatorEnum.IN, ConditionOperatorEnum.NOT_IN]),
  new Set([ConditionOperatorEnum.EXISTS]),
  // Always a FIELD reference plus a sibling dateOffset, so switching to or from it
  // must clear the value.
  new Set([ConditionOperatorEnum.DATE_OFFSET_FROM_FIELD]),
  new Set([
    ConditionOperatorEnum.N_DAYS_IN_PAST,
    ConditionOperatorEnum.N_WEEKS_IN_PAST,
    ConditionOperatorEnum.N_MONTHS_IN_PAST,
    ConditionOperatorEnum.N_YEARS_IN_PAST,
    ConditionOperatorEnum.N_DAYS_IN_FUTURE,
    ConditionOperatorEnum.N_WEEKS_IN_FUTURE,
    ConditionOperatorEnum.N_MONTHS_IN_FUTURE,
    ConditionOperatorEnum.N_YEARS_IN_FUTURE,
  ]),
  new Set([
    ConditionOperatorEnum.FIRST_DAY_OF_MONTH,
    ConditionOperatorEnum.LAST_DAY_OF_MONTH,
  ]),
  new Set([
    ConditionOperatorEnum.BEFORE_CURRENT_MONTH,
    ConditionOperatorEnum.AFTER_CURRENT_MONTH,
  ]),
  new Set([
    ConditionOperatorEnum.N_DAYS_BEFORE_CONTRACT_START,
    ConditionOperatorEnum.N_DAYS_AFTER_CONTRACT_START,
  ]),
  // Length operators take a numeric value; kept in their own set so switching from a
  // text operator (e.g. CONTAINS) clears the stale string value, while switching among
  // the length operators preserves the number.
  new Set([
    ConditionOperatorEnum.LENGTH_GREATER_THAN,
    ConditionOperatorEnum.LENGTH_LESS_THAN,
    ConditionOperatorEnum.LENGTH_EQUAL,
  ]),
];

export const shouldClearValueFromOperatorChange = (
  priorValue: ConditionOperatorEnum,
  newValue: ConditionOperatorEnum,
) => {
  return !compatibleOperatorTypes.some(
    (set) => set.has(priorValue) && set.has(newValue),
  );
};

export const RuleStatusTagStyle: {
  [key in RuleStatusEnum]: {
    color: string;
    bgColor?: string;
    px?: number;
    borderRadius?: number;
    fontStyle?: string;
  };
} = {
  ACTIVE: {
    color: 'tGray.darkGrayPurple',
  },
  INACTIVE: {
    color: 'tRed.base',
  },
  ARCHIVED: {
    color: 'tRed.base',
  },
};

// This should cover pretty much all statuses - we just need to display the text
const STATUS_DISPLAY_OPTIONS = {
  ...OFFERING_STATUS_DISPLAY,
  ...QuoteStatusEnumDisplay,
};

export const listOperators = new Set([
  ConditionOperatorEnum.IN,
  ConditionOperatorEnum.NOT_IN,
]);

export const getNewRule = (entity: EntityGroupEnum): IRuleCondition => {
  // Depending on the entity, we need to set the default condition entity
  const defaultConditionEntityMap: Record<
    EntityGroupEnum,
    RuleConditionEntityEnum
  > = {
    [EntityGroupEnum.QUOTE]: QuoteEntityEnum.QUOTE,
    [EntityGroupEnum.QUOTE_SHARE]: BillGroupEntityEnum.QUOTE,
    [EntityGroupEnum.BILL_GROUP]: BillGroupEntityEnum.BILL_GROUP,
    [EntityGroupEnum.INVOICE]: InvoiceEntityEnum.INVOICE,
    [EntityGroupEnum.CUSTOMER_PORTAL]: CustomerPortalEntityEnum.BILL_GROUP,
  };
  const output: IRuleCondition = {
    entity: defaultConditionEntityMap[entity],
    expressionOperator: ExpressionOperatorEnum.AND,
    customLogic: '',
    conditions: [getNewRuleCondition()],
  };
  return output;
};

export const getNewRuleCondition = (): ICondition => {
  return {
    field: '',
    operator: ConditionOperatorEnum.EQUAL,
    value: {
      type: ConditionOperatorTypeEnum.LITERAL,
      value: '',
    },
  };
};

export const getOperators = (
  ruleFormData: IRuleFormResponse,
  selectedEntity: RuleConditionEntityEnum,
  selectedField: string,
): ConditionOperatorEnum[] => {
  let operations = new Set<ConditionOperatorEnum>();
  const operatorType =
    ruleFormData.fields[selectedEntity]?.fields[selectedField]?.type;
  if (operatorType && ruleFormData.operatorTypes[operatorType]?.operators) {
    operations = new Set(
      Object.keys(
        ruleFormData.operatorTypes[operatorType]?.operators || {},
      ) as ConditionOperatorEnum[],
    );
  }

  return ruleFormData.operators.filter((value) => operations.has(value));
};

/**
 * Get value type for selected field and operator
 * This determines what kind of input will be rendered
 * And how to fetch related data if applicable
 */
export function getValueType(
  operatorTypes: TypeOperatorByFieldType,
  selectedFieldMetadata: Maybe<RuleFormField>,
  selectedOperator: Maybe<ConditionOperatorEnum>,
  tenantId: string,
): TypeOperator & {
  referenceOptions?: Pick<
    MCustomSelectProps,
    'itemTitle' | 'itemValue' | 'endpoint'
  >;
} {
  if (!selectedFieldMetadata || !selectedOperator) {
    return { type: TypeOperatorEnum.TEXT };
  }

  const typeOperator =
    operatorTypes[selectedFieldMetadata.type]?.operators?.[selectedOperator];

  if (typeOperator) {
    if (
      typeOperator.type === TypeOperatorEnum.FIELD_VALUES_LIST &&
      selectedFieldMetadata.values
    ) {
      return {
        type: TypeOperatorEnum.LIST,
        options: selectedFieldMetadata.values,
      };
    }
    if (
      typeOperator.type === TypeOperatorEnum.REFERENCE_LIST &&
      selectedFieldMetadata.referenceEntity
    ) {
      const referenceOptions: ReferenceOptions = getReferenceOptions(
        selectedFieldMetadata.referenceEntity,
        tenantId,
      );
      referenceOptions.popOverContentProps = { width: '100%' };

      return { type: TypeOperatorEnum.REFERENCE_LIST, referenceOptions };
    }
    return typeOperator;
  }
  return { type: TypeOperatorEnum.TEXT };
}

/**
 * Get reference options for a given reference entity
 * @param referenceEntity - The reference entity to get options for
 * @param tenantId - The tenant ID
 * @returns The reference options
 */
export const getReferenceOptions = (
  referenceEntity: ReferenceEntityEnum,
  tenantId: string,
) => {
  let referenceOptions: ReferenceOptions = {};

  const listApiConfig: GetListApiConfig = {
    first: 0,
    rows: 50,
    page: 0,
    sortField: 'name',
    sortOrder: 1,
  };
  if (referenceEntity === ReferenceEntityEnum.ACCOUNT) {
    listApiConfig.sortField = 'accountName';
    // query
    referenceOptions = {
      endpoint: `/api/accounts/search`,
      additionalSearchParams: composeGetQuery(listApiConfig),
      getByIdEndpointFn: (recordId) => `/api/accounts/${recordId}`,
      itemSearch: 'query',
      omitContainsItemSearch: true,
      itemTitle: 'accountName',
      itemValue: 'id',
      renderItemContent: renderItemContentForAccount,
    };
  } else if (referenceEntity === ReferenceEntityEnum.CURRENCY) {
    listApiConfig.sortField = 'code';
    referenceOptions = {
      endpoint: `/api/currencies`,
      additionalSearchParams: composeGetQuery(listApiConfig),
      loadAll: true,
      itemTitle: 'code',
      itemValue: 'code',
      renderItemContent: renderItemForCurrency,
    };
  } else if (referenceEntity === ReferenceEntityEnum.OFFERING) {
    referenceOptions = {
      endpoint: `/api/offerings/summary`,
      additionalSearchParams: composeGetQuery(listApiConfig),
      loadAll: true,
      getByIdEndpointFn: (recordId) => `/api/offerings/summary?id=${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => OFFERING_TYPES_ENUM_DISPLAY[item.type],
        row3Item1Fn: (item) => item.id,
        row3Item2Fn: (item) =>
          STATUS_DISPLAY_OPTIONS[item.status] || item.status,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.PRODUCT) {
    referenceOptions = {
      endpoint: `/api/products`,
      loadAll: true,
      additionalSearchParams: composeGetQuery(listApiConfig),
      getByIdEndpointFn: (recordId) => `/api/products/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => PRODUCT_TYPE_DISPLAY_SHORT[item.productType],
        row3Item1Fn: (item) => item.id,
        row3Item2Fn: (item) =>
          STATUS_DISPLAY_OPTIONS[item.status] || item.status,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.RATE) {
    referenceOptions = {
      endpoint: `/api/rates`,
      additionalSearchParams: composeGetQuery(listApiConfig),
      getByIdEndpointFn: (recordId) => `/api/rates/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) =>
          RATE_BILLING_FREQUENCY_DISPLAY[item.billingFrequency],
        row2Item2Fn: (item) =>
          SUBSCRIPTION_TIMING_OPTIONS[item.subscriptionTiming],
        row3Item1Fn: (item) => item.id,
        row3Item2Fn: (item) =>
          STATUS_DISPLAY_OPTIONS[item.status] || item.status,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.RULE) {
    listApiConfig.sortField = 'name';
    referenceOptions = {
      endpoint: `/rules-v3/rules`,
      additionalSearchParams: composeGetQuery(listApiConfig, {
        type: 'CONDITIONAL_TERM',
      }),
      loadAll: true,
      getByIdEndpointFn: (recordId) => `/rules-v3/rules/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) =>
          RuleStatusEnumDisplay[item.status] || item.status,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.TEAM) {
    listApiConfig.sortField = 'name';
    referenceOptions = {
      endpoint: `/api/teams`,
      additionalSearchParams: composeGetQuery(listApiConfig),
      loadAll: true,
      getByIdEndpointFn: (recordId) => `/api/teams/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => `${TEAM_TYPE_ENUM_DISPLAY[item.type]} Team`,
        row3Item1Fn: (item) => item.id,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.USER) {
    listApiConfig.sortField = 'name';
    referenceOptions = {
      endpoint: `/api/tenants/${tenantId}/users`,
      additionalSearchParams: composeGetQuery(listApiConfig, {
        type: { in: [UserTypeEnum.APP, UserTypeEnum.USER] },
      }),
      loadAll: true,
      getByIdEndpointFn: (recordId) =>
        `/api/tenants/${tenantId}/users/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemContentForOwner,
    };
  } else if (referenceEntity === ReferenceEntityEnum.DISCOUNTS) {
    listApiConfig.sortField = 'name';
    referenceOptions = {
      endpoint: `api/discounts`,
      additionalSearchParams: composeGetQuery(listApiConfig, {
        accountId: 'eq:null',
      }),
      loadAll: true,
      getByIdEndpointFn: (recordId) => `/api/discounts/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => item.id,
        row2Item2Fn: (item) => DISCOUNT_STATUS_ENUM_DISPLAY[item.status],
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.LEGAL_ENTITY) {
    listApiConfig.sortField = 'name';
    referenceOptions = {
      endpoint: `api/configurations/legalEntities`,
      additionalSearchParams: composeGetQuery(listApiConfig, {}),
      loadAll: true,
      getByIdEndpointFn: (recordId) =>
        `/api/configurations/legalEntities/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => item.id,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.DUNNING_PROCESS) {
    referenceOptions = {
      endpoint: `api/dunning/dunningProcess`,
      additionalSearchParams: composeGetQuery(listApiConfig, {}),
      loadAll: true,
      getByIdEndpointFn: (recordId) =>
        `/api/dunning/dunningProcess/${recordId}`,
      itemTitle: 'name',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => item.id,
      }),
    };
  } else if (referenceEntity === ReferenceEntityEnum.QUOTE_DOCUMENT_TYPE) {
    listApiConfig.sortField = 'label';
    referenceOptions = {
      endpoint: `/api/configurations/selectOptions/search?status=ACTIVE&scope=QUOTE_DOCUMENT_TYPE`,
      additionalSearchParams: composeGetQuery(listApiConfig, {}),
      loadAll: true,
      getByIdEndpointFn: (recordId) =>
        `/api/configurations/selectOptions/QUOTE_DOCUMENT_TYPE/${recordId}`,
      itemTitle: 'label',
      itemValue: 'id',
      renderItemContent: renderItemDynamicContent({
        row2Item1Fn: (item) => item.label,
      }),
    };
  }

  return referenceOptions;
};

export const renderItemContentForItemsWithIdName: ReferenceOptions['renderItemContent'] =
  ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
      >
        <MFlex justifyContent="space-between" w="100%">
          <Highlighter
            title={item.name}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.name}
            searchWords={[query]}
            whiteSpace="normal"
          />
        </MFlex>
        <MText color="tGray.darkPurple" fontSize="xs">
          {item.id}
        </MText>
      </MFlex>
    );
  };

export const renderItemContentForItemsWithIdNameStatus: ReferenceOptions['renderItemContent'] =
  ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
      >
        <MFlex justifyContent="space-between" w="100%">
          <Highlighter
            title={item.name}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.name}
            searchWords={[query]}
            whiteSpace="normal"
          />
        </MFlex>
        <MFlex justifyContent="space-between">
          <MText color="tGray.darkPurple" fontSize="xs">
            {item.id}
          </MText>
          <MText color="tGray.darkPurple" fontSize="xs">
            {STATUS_DISPLAY_OPTIONS[item.status] || item.status}
          </MText>
        </MFlex>
      </MFlex>
    );
  };

export const renderItemContentForAccount: ReferenceOptions['renderItemContent'] =
  ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
        minW="200px"
      >
        <MFlex flexDir="column" w="100%">
          <Highlighter
            title={item.accountName}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.accountName}
            searchWords={[query]}
          />
          <MFlex alignItems="center" justifyContent="space-between">
            <Highlighter
              title={item.id}
              color="tGray.darkPurple"
              fontSize="xs"
              textToHighlight={item.id}
              searchWords={[query]}
            />
            <MText color="tGray.darkPurple" fontSize="xxs">
              {item.accountType === AccountTypeEnum.PARTNER ? 'Partner' : ''}
            </MText>
          </MFlex>
          <Highlighter
            title={item.customId}
            color="tGray.darkPurple"
            fontSize="xs"
            textToHighlight={item.customId}
            searchWords={[query]}
          />
        </MFlex>
      </MFlex>
    );
  };

export const renderItemContentForAccountHierarchy: ReferenceOptions['renderItemContent'] =
  ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
        minW="200px"
      >
        <MFlex
          flexDir="column"
          w="100%"
          pl={item.level > 0 ? item.level * 2 : '0'}
        >
          <Highlighter
            title={item.name}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.name}
            searchWords={[query]}
          />
        </MFlex>
      </MFlex>
    );
  };

const renderItemDynamicContent = ({
  row2Item1Fn,
  row2Item2Fn,
  row3Item1Fn,
  row3Item2Fn,
}: {
  row2Item1Fn: (item: any) => ReactNode;
  row2Item2Fn?: (item: any) => ReactNode;
  row3Item1Fn?: (item: any) => ReactNode;
  row3Item2Fn?: (item: any) => ReactNode;
}) => {
  const fn: ReferenceOptions['renderItemContent'] = ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
      >
        <MFlex justifyContent="space-between" w="100%">
          <Highlighter
            title={item.name}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.name}
            searchWords={[query]}
            whiteSpace="normal"
          />
        </MFlex>
        <MFlex justifyContent="space-between" w="100%">
          <MText color="tGray.darkPurple" fontSize="xs">
            {row2Item1Fn(item)}
          </MText>
          {row2Item2Fn && (
            <MText color="tGray.darkPurple" fontSize="xs">
              {row2Item2Fn(item)}
            </MText>
          )}
        </MFlex>
        {row3Item1Fn && (
          <MFlex justifyContent="space-between">
            <MText color="tGray.darkPurple" fontSize="xs">
              {row3Item1Fn(item)}
            </MText>
            {row3Item2Fn && (
              <MText color="tGray.darkPurple" fontSize="xs">
                {row3Item2Fn(item)}
              </MText>
            )}
          </MFlex>
        )}
      </MFlex>
    );
  };
  return fn;
};

export const renderItemForCurrency: ReferenceOptions['renderItemContent'] = ({
  title,
  item,
  isSubtitle,
}) => {
  return (
    <Flex alignItems="center">
      <MText
        color="inherit"
        fontWeight="bold"
        truncate
        lineClamp={1}
        fontSize={isSubtitle ? '12px' : 'sm'}
        mr="2"
      >
        {title}
      </MText>
      <MText
        fontWeight={'normal'}
        color="inherit"
        truncate
        lineClamp={1}
        fontSize={isSubtitle ? '12px' : 'sm'}
      >
        {item.description}
      </MText>
    </Flex>
  );
};

export const renderItemContentForOwner: ReferenceOptions['renderItemContent'] =
  ({ item, query }) => {
    return (
      <MFlex
        direction="column"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        w="100%"
      >
        <MFlex justifyContent="space-between" w="100%">
          <Highlighter
            title={item.name}
            color="tPurple.dark"
            fontSize="sm"
            fontWeight="medium"
            textToHighlight={item.name}
            searchWords={[query]}
            whiteSpace="normal"
          />
        </MFlex>
        <MText color="tGray.darkPurple" fontSize="xs">
          {item.id} ({USER_TYPE_DISPLAY[item.type]})
        </MText>
      </MFlex>
    );
  };

/**
 * Fill in all non-critical fields with default values so a user can test the rule
 * without having to fill in every field
 */
export function prepareRuleForTest(rule: IRule) {
  rule = nullifyEmptyStrings(rule);

  // Ensure that all required fields are initialized if they are not part of rule logic
  rule.name = rule.name || 'Test Rule';
  rule.description = rule.description || 'Test Rule';
  rule.status = rule.status || RuleStatusEnum.ACTIVE;
  switch (rule.action.actionType) {
    case RuleTypeEnum.APPROVAL:
      break;
    case RuleTypeEnum.CONDITIONAL_TERM:
      rule.action.conditionalTerms.terms =
        rule.action.conditionalTerms.terms || 'Test Rule';
      break;
    case RuleTypeEnum.VALIDATION:
      rule.action.validation.message =
        rule.action.validation.message || 'Test Rule';
      break;
    case RuleTypeEnum.RECORD_AUTOMATION:
      if (!rule.action.values || rule.action.values.length === 0) {
        rule.action.values = [];
      }
      if (!rule.action.priority) {
        rule.action.priority = 1;
      }
      break;
  }

  return RuleSchema.safeParse(rule);
}

export const getTitleByType = (type: RuleTypeEnum) => {
  switch (type) {
    case RuleTypeEnum.APPROVAL:
      return {
        single: 'Approval Rule',
        plural: 'Approvals',
      };
    case RuleTypeEnum.VALIDATION:
      return {
        single: 'Validation Rule',
        plural: 'Validations',
      };
    case RuleTypeEnum.CONDITIONAL_TERM:
      return {
        single: 'Conditional Term',
        plural: 'Conditional Terms',
      };
    case RuleTypeEnum.RECORD_AUTOMATION:
      return {
        single: 'Record Automation Rule',
        plural: 'Record Automations',
      };
  }
};

export const RULE_DATA_KEY_V3: {
  [key in RuleTypeEnum]: {
    queryKey:
      | 'approvals'
      | 'validations'
      | 'conditionalTerms'
      | 'recordAutomation';
  };
} = {
  [RuleTypeEnum.APPROVAL]: { queryKey: 'approvals' },
  [RuleTypeEnum.VALIDATION]: { queryKey: 'validations' },
  [RuleTypeEnum.CONDITIONAL_TERM]: { queryKey: 'conditionalTerms' },
  [RuleTypeEnum.RECORD_AUTOMATION]: { queryKey: 'recordAutomation' },
};

// Shared Test Result Components
export const SuccessItem: FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <MFlex alignItems="center" mt={2}>
    <MIcon as={MdCheckCircle} color="tGreen.approval" mr={2} />
    <MText color="tGreen.approval">{children}</MText>
  </MFlex>
);

export const FailureItem: FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <MFlex alignItems="center" mt={2}>
    <MIcon as={MdError} color="tRed.base" mr={2} />
    <MText color="tRed.base">{children}</MText>
  </MFlex>
);

export const ValidationResults = ({
  validation,
}: {
  validation: ValidateRuleResponse;
}) => {
  const { valid, message } = validation;
  if (valid) {
    return <SuccessItem>Rule configuration is valid</SuccessItem>;
  }
  return <FailureItem>Rule configuration is invalid: {message}</FailureItem>;
};

export const TestResultStatus = ({ value }: { value: boolean }) => {
  if (value) {
    return <SuccessItem>Rule evaluated to true</SuccessItem>;
  }
  return <FailureItem>Rule evaluated to false</FailureItem>;
};

export const TestConditionStatus = ({
  result,
  index,
}: {
  result: boolean;
  index: number;
}) => {
  if (result) {
    return <SuccessItem>Condition {index + 1} evaluated to true</SuccessItem>;
  }
  return <FailureItem> Condition {index + 1} evaluated to false</FailureItem>;
};

export const getTempType = (value: any, isDate: boolean = false) => {
  if (value === ConditionOperatorTypeEnum.FIELD) {
    return value;
  }
  return isDate
    ? ConditionOperatorTypeEnum.DATE
    : ConditionOperatorTypeEnum.LITERAL;
};

export const isDateFieldType = (fieldType: FieldTypeEnum) => {
  return (
    fieldType === FieldTypeEnum.DATE ||
    fieldType === FieldTypeEnum.DATE_EXTENDED
  );
};

export const VALUE_FIELD_TYPES = [
  { key: ConditionOperatorTypeEnum.LITERAL, label: 'Value' },
  { key: ConditionOperatorTypeEnum.FIELD, label: 'Field' },
];

export const getRecordAutomationValueFields = (
  ruleFormData: IRuleFormResponse,
  ruleTarget: EntityGroupEnum,
  selectedField: RecordAutomationActionFields | undefined,
) => {
  const allFields: RuleFormField[] = [];
  const availableFields = ruleFormData.fields;

  Object.keys(availableFields).forEach((entity) => {
    const entityDisplay = RuleEntityEnumDisplay?.[ruleTarget]?.[entity] || '';

    Object.keys(availableFields[entity].fields).forEach((fieldKey) => {
      if (!availableFields[entity].fields[fieldKey].isLookupField) {
        allFields.push({
          ...availableFields[entity].fields[fieldKey],
          label: `${entityDisplay ? `${entityDisplay} -> ` : ''}${availableFields[entity].fields[fieldKey].label}`,
          key: fieldKey,
        });
      }
    });
  });

  switch (selectedField?.type) {
    case RecordAutomationUiFieldTypeEnum.STRING:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.STRING, FieldTypeEnum.LIST].includes(field.type),
      );
    case RecordAutomationUiFieldTypeEnum.BOOLEAN:
    case RecordAutomationUiFieldTypeEnum.BOOLEAN_LIST:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.BOOLEAN, FieldTypeEnum.BOOLEAN_LIST].includes(
          field.type,
        ),
      );
    case RecordAutomationUiFieldTypeEnum.DATE:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.DATE].includes(field.type),
      );
    case RecordAutomationUiFieldTypeEnum.NUMBER:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.NUMBER].includes(field.type),
      );
    case RecordAutomationUiFieldTypeEnum.REFERENCE:
      return allFields.filter(
        (field: RuleFormField) =>
          [FieldTypeEnum.REFERENCE].includes(field.type) &&
          field.referenceEntity === selectedField?.referenceEntity,
      );
    case RecordAutomationUiFieldTypeEnum.LIST:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.LIST, FieldTypeEnum.STRING].includes(field.type),
      );
    case RecordAutomationUiFieldTypeEnum.EMAIL_ARRAY:
      return allFields.filter((field: RuleFormField) =>
        [FieldTypeEnum.STRING_ARRAY].includes(field.type),
      );
    default:
      return [];
  }
};

export interface FormErrorMessage {
  /** Dotted form path, e.g. `action.validation.fields`. For logs only. */
  path: string;
  message: string;
}

/**
 * Flattens a react-hook-form error tree into `{ path, message }` pairs.
 *
 * react-hook-form nests errors to mirror the form shape, so a failure deep in
 * the action can otherwise be invisible — the submit button just does nothing.
 */
export const collectFormErrorMessages = (
  errors: unknown,
  path: string[] = [],
): FormErrorMessage[] => {
  if (!errors || typeof errors !== 'object') {
    return [];
  }

  const node = errors as Record<string, any>;
  if (typeof node.message === 'string' && node.message.length > 0) {
    return [{ path: path.join('.'), message: node.message }];
  }

  return Object.entries(node).flatMap(([key, value]) => {
    if (key === 'ref' || key === 'type' || key === 'types') {
      return [];
    }
    return collectFormErrorMessages(value, [...path, key]);
  });
};

/**
 * User-facing toast body. Deliberately drops the paths —
 * `action.validation.fields` means nothing to an admin — and de-duplicates
 * repeated messages.
 */
export const getFormErrorToastDetail = (messages: FormErrorMessage[]) => {
  const unique = [...new Set(messages.map(({ message }) => message))];
  return unique.length > 0
    ? unique.join(' • ')
    : 'Some fields on this rule are invalid.';
};
